import { ReactNode, useId } from 'react';
import { Input } from '@/common/components/ui/input';
import { Label } from '@/common/components/ui/label';
import { cn } from '@/lib/utils';

export interface NumberFieldProps {
  value: number | null;
  onValueChange: (value: number | null) => void;
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  onBlur?: () => void;
}

const NumberField = ({
  value,
  onValueChange,
  label,
  helperText,
  error,
  fullWidth,
  min,
  max,
  step,
  disabled,
  required,
  placeholder,
  onBlur,
}: NumberFieldProps) => {
  const id = useId();

  return (
    <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
      {label && (
        <Label htmlFor={id} className={cn(error && 'text-destructive')}>
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
      )}
      <Input
        id={id}
        type="number"
        value={value ?? ''}
        onChange={(e) => {
          const parsed = parseFloat(e.target.value);
          onValueChange(e.target.value === '' || isNaN(parsed) ? null : parsed);
        }}
        onBlur={onBlur}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        className={cn(error && 'border-destructive focus-visible:ring-destructive')}
      />
      {helperText && (
        <span className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {helperText}
        </span>
      )}
    </div>
  );
};

export default NumberField;
